import { Inject, Injectable } from '@angular/core';
import {
  RealtimeClientCommandName,
  RealtimeCommandAcceptedPayload,
  RealtimeCommandCompletedPayload,
  RealtimeCommandQueuePayload,
  RealtimeCommandRejectedPayload,
  RealtimeLinkedPreviewEntity,
} from '@common/api/realtime';
import { AppService } from '@ep-om/app.service';
import { AuthService } from '@ep-om/project/auth/auth.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Manager, Socket } from 'socket.io-client';
import * as msgpackParser from 'socket.io-msgpack-parser';
import { v4 } from 'uuid';
import {
  REALTIME_V2_BUSY_BASE_DELAY_MS,
  REALTIME_V2_BUSY_MAX_DELAY_MS,
  REALTIME_V2_BUSY_MAX_RETRIES,
  REALTIME_V2_COMMAND_TIMEOUT_MS,
  REALTIME_V2_MAX_INFLIGHT_THROTTLED_COMMANDS,
  REALTIME_V2_THROTTLED_COMMAND_MIN_GAP_MS,
  REALTIME_V2_THROTTLED_COMMAND_TIMEOUT_MS,
  RealtimeAckError,
  RealtimeAckMessage,
  RealtimeAckResponse,
  RealtimeClientCommand,
  RealtimeCursor,
  RealtimeEntityCommandPayload,
  RealtimeFeatureCommandPayload,
  RealtimeServerToClientMessage,
  RealtimeV2CommandFailure,
  RealtimeV2CommandInput,
  RealtimeV2CommandOptions,
  RealtimeV2AdmissionEvent,
  RealtimeV2CommandResult,
  RealtimeV2PendingCommand,
  RealtimeV2RequestAck,
  RealtimeV2RequestHandler,
  resolveThrottledScopeKey,
  toRealtimeAckError,
} from './realtime-v2.models';

@Injectable({
  providedIn: 'root',
})
export class RealtimeV2SocketService {
  private manager: Manager;
  private socket: Socket;
  private readonly pendingCommands = new Map<string, RealtimeV2PendingCommand>();
  private readonly _messages$ = new Subject<RealtimeServerToClientMessage>();
  private readonly _admissionEvents$ = new Subject<RealtimeV2AdmissionEvent>();
  private readonly _disconnected$ = new Subject<void>();
  private readonly _reconnect$ = new Subject<void>();
  private requestHandler: RealtimeV2RequestHandler;
  private requestedConnection = false;
  // Numero di comandi attualmente in retry per via di un `busy` del server.
  private busyActive = 0;
  // --- Coda locale (backpressure) dei comandi di ingresso scope. Vedi
  // REALTIME_V2_MAX_INFLIGHT_THROTTLED_COMMANDS per il razionale. ---
  private readonly throttledQueue: RealtimeV2PendingCommand[] = [];
  private throttledInFlight = 0;
  private lastThrottledSendAtMs = 0;
  private throttledPumpTimer: ReturnType<typeof setTimeout> | undefined;
  // scopeKey → ingresso già in volo o in coda locale, per coalescere le richieste identiche.
  private readonly throttledByScope = new Map<string, { requestId: string; signature: string; promise: Promise<any> }>();

  public readonly connected$ = new BehaviorSubject<boolean>(false);
  public readonly messages$: Observable<RealtimeServerToClientMessage> = this._messages$.asObservable();
  // True quando almeno un comando è in retry per overload del server (coda di ammissione piena).
  // La UI può agganciarsi per mostrare un indicatore "realtime rallentato".
  public readonly degraded$ = new BehaviorSubject<boolean>(false);
  /**
   * Eventi grezzi di ammissione (coda del server) per gli ingressi di scope, già correlati allo
   * `scopeKey`. È una primitiva: il socket non mantiene lo stato di attesa, lo deriva
   * RealtimeV2AdmissionService. Copre tutti i comandi throttled (home, project, issue, linked-preview).
   */
  public readonly admissionEvents$: Observable<RealtimeV2AdmissionEvent> = this._admissionEvents$.asObservable();

  public get currentSocketId(): string | null {
    return this.socket?.id ?? null;
  }

  constructor(
    @Inject('SOCKET_IO_ADDRESS') private address: string,
    private appService: AppService,
    private authService: AuthService,
  ) {
    // NB: la connessione del socket v2 NON è decisa qui. È guidata da RealtimeV2RuntimeService, che
    // la attiva solo quando il motore è 'v2' (arbitro) E c'è una sessione loggata, e solo DOPO aver
    // registrato gli store Akita (altrimenti un entity_sync in arrivo esploderebbe). Connettersi qui
    // sul solo token significava restare connessi al v2 anche con motore 'v1' -> doppia connessione
    // v1+v2. La sola eccezione gestita internamente è il reconnect dopo un drop (sotto).

    this._reconnect$.pipe(debounceTime(5000)).subscribe(() => {
      if (this.requestedConnection && this.authService.isLoggedIn()) {
        this.connect();
      }
    });
  }

  public connect(): void {
    const token = this.authService.authQuery.getAccessToken();
    this.requestedConnection = true;

    if (!token) {
      return;
    }

    this.ensureSocket(token);

    if (this.socket.connected) {
      return;
    }

    this.manager.opts = {
      ...this.manager.opts,
      query: { token },
    };
    this.socket.connect();
  }

  public disconnect(): void {
    this.requestedConnection = false;
    this.rejectAllPendingCommands(this.commandFailure('Realtime V2 disconnected', 'disconnected'));

    if (this.socket) {
      this.socket.disconnect();
    }

    this._disconnected$.next();
    this.connected$.next(false);
  }

  public stopOnDisconnectionPipe<T>() {
    return takeUntil<T>(this._disconnected$);
  }

  public sendCommand<TCommand extends RealtimeClientCommandName, TBody = any>(
    input: RealtimeV2CommandInput<TCommand>,
  ): Promise<RealtimeV2CommandResult<TBody>> {
    if (!this.socket || !this.socket.connected) {
      return Promise.reject(this.commandFailure('Realtime V2 socket is not connected', 'not_connected'));
    }

    // Gli ingressi di scope passano dalla coda locale; tutto il resto vola diretto.
    const scopeKey = resolveThrottledScopeKey(input.type, input.payload);

    // Coalescing: un ingresso IDENTICO nello stesso scope già in volo (o in attesa nella coda
    // locale) non viene ri-inviato, il chiamante condivide l'esito di quello esistente. È la prima
    // linea di backpressure: taglia le richieste duplicate che il server rifiuterebbe comunque.
    // Il confronto include il payload perché due enter sullo stesso scope non sono intercambiabili:
    // un resync forzato riparte con cursori vuoti per farsi mandare tutto, e non deve mai ereditare
    // il delta di un enter già in volo. Salta il coalescing quando il chiamante ha imposto un `id`:
    // sta correlando lui la richiesta.
    const signature = scopeKey && !input.id ? JSON.stringify(input.payload ?? null) : null;
    if (scopeKey && signature !== null) {
      const inflight = this.throttledByScope.get(scopeKey);
      if (inflight && inflight.signature === signature) {
        return inflight.promise as Promise<RealtimeV2CommandResult<TBody>>;
      }
    }

    const command: RealtimeClientCommand<TCommand> = {
      id: input.id || v4(),
      type: input.type,
      expectsCompletion: input.expectsCompletion,
      payload: input.payload,
    };

    const promise = new Promise<RealtimeV2CommandResult<TBody>>((resolve, reject) => {
      // Il budget copre attesa locale + coda di ammissione del server + retry su `busy`, quindi per
      // gli ingressi di scope è più largo del timeout di una singola andata/ritorno.
      const timeoutMs = input.timeoutMs
        ?? (scopeKey ? REALTIME_V2_THROTTLED_COMMAND_TIMEOUT_MS : REALTIME_V2_COMMAND_TIMEOUT_MS);
      const timeoutId = setTimeout(() => {
        this.finalizePending(command.id);
        reject(this.commandFailure('Realtime V2 command timed out', 'timeout', {
          requestId: command.id,
          command: command.type,
        }));
      }, timeoutMs);

      const pending: RealtimeV2PendingCommand = {
        command: command.type,
        envelope: command,
        waitForCompletion: command.expectsCompletion === true && input.waitForCompletion !== false,
        timeoutId,
        resolve,
        reject,
        scopeKey,
      };
      this.pendingCommands.set(command.id, pending);

      if (scopeKey) {
        this.enqueueThrottled(pending);
        return;
      }

      this.socket.emit('ws_cmd', command);
    });

    if (scopeKey && signature !== null) {
      // Un enter diverso sullo stesso scope sostituisce il precedente come candidato al coalescing:
      // è il più aggiornato, e quello vecchio resta comunque vivo per chi lo sta già aspettando.
      this.throttledByScope.set(scopeKey, { requestId: command.id, signature, promise });
    }

    return promise;
  }

  public enterHome(cursors?: RealtimeCursor[], options?: RealtimeV2CommandOptions) {
    console.log(`[REALTIME V2] enter home`, cursors);
    return this.sendCommand({ ...options, type: 'enterHome', payload: { cursors } });
  }

  public enterProject(projectId: string, cursors?: RealtimeCursor[], options?: RealtimeV2CommandOptions) {
    console.log(`[REALTIME V2] enter project`, cursors);
    return this.sendCommand({ ...options, type: 'enterProject', payload: { projectId, cursors } });
  }

  public leaveProject(projectId: string, options?: RealtimeV2CommandOptions) {
    return this.sendCommand({ ...options, type: 'leaveProject', payload: { projectId } });
  }

  public enterIssue(issueId: string, cursors?: RealtimeCursor[], options?: RealtimeV2CommandOptions) {
    return this.sendCommand({ ...options, type: 'enterIssue', payload: { issueId, cursors } });
  }

  public leaveIssue(issueId: string, options?: RealtimeV2CommandOptions) {
    return this.sendCommand({ ...options, type: 'leaveIssue', payload: { issueId } });
  }

  public enterLinkedPreview(payload: {
    previewKey?: string;
    linkedIssueId: string;
    linkedProjectId?: string;
    parentProjectId?: string;
    taskTypeId?: string;
    interfaceType?: string;
    linkInterfaceName?: string;
    entities: RealtimeLinkedPreviewEntity[];
    cursors?: RealtimeCursor[];
  }, options?: RealtimeV2CommandOptions) {
    return this.sendCommand({ ...options, type: 'enterLinkedPreview', payload });
  }

  public leaveLinkedPreview(payload: { previewKey?: string; linkedIssueId?: string }, options?: RealtimeV2CommandOptions) {
    return this.sendCommand({ ...options, type: 'leaveLinkedPreview', payload });
  }

  public entityCommand(payload: RealtimeEntityCommandPayload, options?: RealtimeV2CommandOptions) {
    return this.sendCommand({ ...options, type: 'entityCommand', payload });
  }

  public featureCommand(payload: RealtimeFeatureCommandPayload, options?: RealtimeV2CommandOptions) {
    return this.sendCommand({ ...options, type: 'featureCommand', payload });
  }

  public registerRequestHandler(handler: RealtimeV2RequestHandler): void {
    if (this.requestHandler && this.requestHandler !== handler) {
      throw new Error('Realtime V2 request handler already registered');
    }

    this.requestHandler = handler;
  }

  // Bootstrap: il socket v2 non si connette se il backend rifiuta il token (es. access token
  // scaduto). In quel caso il backend non può eleggere nessuna main session e il refresh non
  // partirebbe mai. Una sola tab del browser si auto-elegge (Web Locks) per far girare la
  // pipeline di refresh. Se anche il refresh token è scaduto, la pipeline auth forza il logout.
  private async tryFallbackMainElection(): Promise<void> {
    if (this.appService.mainTab$.value) {
      return; // già main (eletta dal backend o già auto-eletta)
    }
    const accessToken = this.authService.authQuery.getAccessToken();
    if (!accessToken) {
      return; // non loggato: nulla da rinnovare
    }
    const expires = this.authService.getExpirationFromToken(accessToken);
    const aboutToExpire = (expires.getTime() - Date.now()) < 5000;
    if (!aboutToExpire) {
      return; // token ancora valido: connect_error transitorio, niente auto-elezione
    }
    const online = await this.appService.amIOnline();
    if (!online) {
      return;
    }
    await this.appService.claimMainTabLeadership();
  }

  private ensureSocket(token: string): void {
    if (this.socket) {
      return;
    }

    // Parametri del socket v2 dal backend (settings.json -> realtime), non piu' da environment.
    const realtime = this.appService.SETTINGS?.realtime;
    this.manager = new Manager(`${this.address}`, {
      autoConnect: false,
      path: realtime?.socketPath || '/socket.io-v2',
      transports: ['websocket'],
      reconnection: false,
      ...(realtime?.parser === 'msgpack' && { parser: msgpackParser }),
      query: { token },
    });
    this.socket = this.manager.socket('/');

    this.socket.on('connect', async () => {
      await this.appService.check();
      this.connected$.next(true);
    });

    this.socket.on('disconnect', () => {
      this.appService.setOffline();
      this.rejectAllPendingCommands(this.commandFailure('Realtime V2 disconnected', 'disconnected'));
      this._disconnected$.next();
      this.connected$.next(false);
      if (this.requestedConnection) {
        this._reconnect$.next();
      }
    });

    this.socket.on('connect_error', async (error) => {
      console.log('[REALTIME-V2] socket connect error', error);
      await this.tryFallbackMainElection();
      if (this.requestedConnection) {
        this._reconnect$.next();
      }
    });

    this.socket.on('ws_msg', (message: RealtimeServerToClientMessage) => {
      this.handleMessage(message);
      this._messages$.next(message);
    });

    this.socket.on('ws_req', (request: RealtimeAckMessage) => this.handleRequest(request));
  }

  private handleMessage(message: RealtimeServerToClientMessage): void {
    switch (message.type) {
      case 'command_accepted':
        this.handleCommandAccepted(message.payload);
        break;
      case 'command_queued':
      case 'command_queue_updated':
        this.handleCommandQueue(message.payload);
        break;
      case 'scope_ready':
        this.handleScopeReady(message.payload.scopeKey);
        break;
      case 'command_rejected':
        this.handleCommandRejected(message.payload);
        break;
      case 'command_completed':
        this.handleCommandCompleted(message.payload);
        break;
      default:
        break;
    }
  }

  private handleRequest(request: RealtimeAckMessage): void {
    const requestContext = { event: request.payload?.type };

    if (!this.requestHandler) {
      console.error('[REALTIME-V2] request received without handler', request);
      this.emitRequestAck(request.id, false, toRealtimeAckError('request received without handler', requestContext));
      return;
    }

    const ack = this.createRequestAck(request.id, requestContext);

    try {
      const result = this.requestHandler(request, ack);
      if (this.isPromiseLike(result)) {
        result.catch((error) => {
          console.error('[REALTIME-V2] async request handler failed', request, error);
          ack(false, error);
        });
      }
    } catch (error) {
      console.error('[REALTIME-V2] request handler failed', request, error);
      ack(false, error);
    }
  }

  private createRequestAck(id: string, requestContext: Record<string, any>): RealtimeV2RequestAck {
    let acked = false;

    return (ok: boolean, error?: unknown, context?: Record<string, any>) => {
      if (acked) {
        console.warn('[REALTIME-V2] duplicate request ack skipped', { id, ok });
        return;
      }

      acked = true;
      // L'errore viaggia solo sul rifiuto: sul path felice non c'è nulla da riportare.
      this.emitRequestAck(id, ok, ok ? undefined : toRealtimeAckError(error, { ...requestContext, ...context }));
    };
  }

  private emitRequestAck(id: string, ok: boolean, error?: RealtimeAckError): void {
    if (!this.socket || !this.socket.connected) {
      throw this.commandFailure('Realtime V2 socket is not connected', 'not_connected');
    }

    const response: RealtimeAckResponse = { id, ok, ...(error && { error }) };
    this.socket.emit('ws_req_ack', response);
  }

  private isPromiseLike<T>(value: T | Promise<T>): value is Promise<T> {
    return !!value && typeof (value as Promise<T>).catch === 'function';
  }

  private handleCommandAccepted(payload: RealtimeCommandAcceptedPayload): void {
    const pending = this.pendingCommands.get(payload.requestId);
    if (pending) {
      const scopeKey = pending.scopeKey;
      if (scopeKey) {
        this._admissionEvents$.next({ kind: 'accepted', scopeKey, requestId: payload.requestId, command: payload.command });
      }
    }
    if (!pending || pending.waitForCompletion) {
      return;
    }

    this.finalizePending(payload.requestId);
    pending.resolve({
      requestId: payload.requestId,
      command: payload.command,
      accepted: true,
      completed: false,
      body: payload.body,
    });
  }

  private handleCommandRejected(payload: RealtimeCommandRejectedPayload): void {
    const requestId = payload.requestId;
    if (!requestId) {
      return;
    }

    const pending = this.pendingCommands.get(requestId);
    if (!pending) {
      return;
    }

    // `busy`: la coda di ammissione del server è satura. Ritentiamo con backoff invece di fallire.
    // Durante i retry non chiudiamo l'attesa di ammissione: il comando è ancora vivo.
    if (payload.code === 'busy' && this.scheduleBusyRetry(requestId, pending, payload)) {
      return;
    }

    const scopeKey = pending.scopeKey;
    if (scopeKey) {
      this._admissionEvents$.next({ kind: 'settled', scopeKey, requestId, command: pending.command });
    }

    this.finalizePending(requestId);
    pending.reject(this.commandFailure(payload.message, payload.code, payload.details));
  }

  private handleCommandCompleted(payload: RealtimeCommandCompletedPayload): void {
    const pending = this.pendingCommands.get(payload.requestId);
    if (!pending) {
      return;
    }

    this.finalizePending(payload.requestId);

    if (!payload.ok) {
      pending.reject(this.commandFailure(
        payload.error?.message || 'Realtime command failed',
        payload.error?.code || 'command_failed',
        payload.error?.details,
      ));
      return;
    }

    pending.resolve({
      requestId: payload.requestId,
      command: payload.command,
      accepted: true,
      completed: true,
      body: payload.body,
    });
  }

  private rejectAllPendingCommands(error: RealtimeV2CommandFailure): void {
    const rejects: Array<(error: RealtimeV2CommandFailure) => void> = [];
    this.pendingCommands.forEach((pending) => {
      clearTimeout(pending.timeoutId);
      if (pending.retryTimer) {
        clearTimeout(pending.retryTimer);
      }
      rejects.push(pending.reject);
    });
    this.pendingCommands.clear();
    this.busyActive = 0;
    this.degraded$.next(false);
    this.resetThrottledQueue();
    // Reject dopo aver svuotato la mappa, per evitare rientri durante la callback.
    rejects.forEach((reject) => reject(error));
  }

  /**
   * Accoda un ingresso di scope nella finestra di invio locale. La coda è FIFO: chi è stato chiesto
   * prima parte prima, anche se nel frattempo sono comparse altre preview.
   */
  private enqueueThrottled(pending: RealtimeV2PendingCommand): void {
    this.throttledQueue.push(pending);
    this.pumpThrottled();
  }

  /**
   * Manda in volo gli ingressi accodati finché ci sono slot liberi, rispettando la spaziatura minima
   * fra due invii. Non emette mai una raffica: è questo a impedire che N linked-preview montate nello
   * stesso tick arrivino al server tutte insieme e vengano rifiutate in blocco.
   */
  private pumpThrottled(): void {
    if (this.throttledPumpTimer) {
      return; // un pump è già schedulato: riprenderà da lì
    }

    while (this.throttledQueue.length > 0 && this.throttledInFlight < REALTIME_V2_MAX_INFLIGHT_THROTTLED_COMMANDS) {
      const next = this.throttledQueue[0];
      // Scaduto, rigettato o socket caduto nel frattempo: scartalo senza consumare slot.
      if (!this.pendingCommands.has(next.envelope.id)) {
        this.throttledQueue.shift();
        continue;
      }
      if (!this.socket || !this.socket.connected) {
        return; // ci pensa il disconnect handler a rigettare i pending
      }

      const waitMs = this.lastThrottledSendAtMs + REALTIME_V2_THROTTLED_COMMAND_MIN_GAP_MS - Date.now();
      if (waitMs > 0) {
        this.throttledPumpTimer = setTimeout(() => {
          this.throttledPumpTimer = undefined;
          this.pumpThrottled();
        }, waitMs);
        return;
      }

      this.throttledQueue.shift();
      next.inFlight = true;
      this.throttledInFlight += 1;
      this.lastThrottledSendAtMs = Date.now();
      this.socket.emit('ws_cmd', next.envelope);
    }
  }

  /** Libera lo slot occupato da un ingresso e fa ripartire la coda. Idempotente. */
  private releaseThrottledSlot(pending: RealtimeV2PendingCommand): void {
    if (!pending.inFlight) {
      return;
    }
    pending.inFlight = false;
    this.throttledInFlight = Math.max(0, this.throttledInFlight - 1);
    this.pumpThrottled();
  }

  private resetThrottledQueue(): void {
    this.throttledQueue.length = 0;
    this.throttledInFlight = 0;
    this.throttledByScope.clear();
    if (this.throttledPumpTimer) {
      clearTimeout(this.throttledPumpTimer);
      this.throttledPumpTimer = undefined;
    }
  }

  private handleCommandQueue(payload: RealtimeCommandQueuePayload): void {
    const pending = this.pendingCommands.get(payload.requestId);
    if (!pending) {
      return;
    }
    const scopeKey = pending.scopeKey;
    if (!scopeKey) {
      return;
    }
    this._admissionEvents$.next({ kind: 'queued', scopeKey, ...payload });
  }

  private handleScopeReady(scopeKey: string): void {
    this._admissionEvents$.next({ kind: 'ready', scopeKey });
  }

  /**
   * Rimuove un comando in attesa pulendone i timer e aggiornando lo stato `degraded`. Ritorna il
   * pending rimosso (così il chiamante può risolverlo/rigettarlo) o undefined se non presente.
   */
  private finalizePending(requestId: string): RealtimeV2PendingCommand | undefined {
    const pending = this.pendingCommands.get(requestId);
    if (!pending) {
      return undefined;
    }
    this.pendingCommands.delete(requestId);
    clearTimeout(pending.timeoutId);
    if (pending.retryTimer) {
      clearTimeout(pending.retryTimer);
    }
    if (pending.busyCounted) {
      this.endBusy();
    }
    if (pending.scopeKey) {
      // Il coalescing vale solo finché l'ingresso è vivo: un nuovo enter sullo stesso scope dopo la
      // chiusura deve poter ripartire (es. rientro nella preview dopo il leave debounce).
      if (this.throttledByScope.get(pending.scopeKey)?.requestId === requestId) {
        this.throttledByScope.delete(pending.scopeKey);
      }
      this.releaseThrottledSlot(pending);
    }
    return pending;
  }

  /**
   * Pianifica un retry per un comando rifiutato con `busy`. Ritorna true se il retry è stato
   * pianificato, false se i tentativi sono esauriti (il chiamante procederà col reject).
   */
  private scheduleBusyRetry(
    requestId: string,
    pending: RealtimeV2PendingCommand,
    payload: RealtimeCommandRejectedPayload,
  ): boolean {
    const attempts = pending.attempts ?? 0;
    if (attempts >= REALTIME_V2_BUSY_MAX_RETRIES) {
      return false;
    }
    pending.attempts = attempts + 1;
    if (!pending.busyCounted) {
      pending.busyCounted = true;
      this.beginBusy();
    }

    // Il rifiuto libera SUBITO lo slot locale: un altro ingresso in coda può partire mentre questo
    // aspetta il backoff. La finestra di invio resta piena di lavoro utile invece che di ritentativi.
    this.releaseThrottledSlot(pending);

    const delay = this.computeBusyBackoff(pending.attempts, payload.details?.retryAfterMs);
    pending.retryTimer = setTimeout(() => {
      pending.retryTimer = undefined;
      // Il comando potrebbe essere già stato risolto/timeout o il socket essersi disconnesso.
      if (!this.pendingCommands.has(requestId)) {
        return;
      }
      if (!this.socket || !this.socket.connected) {
        return; // ci penserà il disconnect handler a rigettare i pending
      }
      // Il retry rientra dalla porta principale: si rimette in coda e riparte quando c'è uno slot,
      // così non scavalca gli ingressi in attesa né ricrea la raffica.
      if (pending.scopeKey) {
        this.enqueueThrottled(pending);
        return;
      }
      this.socket.emit('ws_cmd', pending.envelope);
    }, delay);
    return true;
  }

  private computeBusyBackoff(attempt: number, suggestedMs?: number): number {
    // `retryAfterMs` è un pavimento, non il valore del backoff: prima di quello il server sa già che
    // è inutile ritentare. Prima veniva usato come BASE dell'esponenziale, il che rendeva i tentativi
    // successivi lunghissimi e faceva scadere il comando invece di riprovare.
    const floor = Math.min(Math.max(suggestedMs ?? 0, 0), REALTIME_V2_BUSY_MAX_DELAY_MS);
    const exp = Math.min(REALTIME_V2_BUSY_BASE_DELAY_MS * Math.pow(2, attempt - 1), REALTIME_V2_BUSY_MAX_DELAY_MS);
    const target = Math.max(exp, floor);
    // Jitter additivo (0..+50%): due ingressi rifiutati nello stesso istante ritentano in momenti
    // diversi anche quando il server suggerisce a entrambi lo stesso `retryAfterMs`.
    return Math.round(target + Math.random() * target * 0.5);
  }

  private beginBusy(): void {
    this.busyActive += 1;
    if (this.busyActive === 1) {
      this.degraded$.next(true);
    }
  }

  private endBusy(): void {
    this.busyActive = Math.max(0, this.busyActive - 1);
    if (this.busyActive === 0) {
      this.degraded$.next(false);
    }
  }

  private commandFailure(message: string, code: string, details?: Record<string, any>): RealtimeV2CommandFailure {
    return {
      message,
      code,
      ...(details && { details }),
    };
  }
}
